import type { JSX } from 'react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { AdminDashboardSummary } from '@echo-work-platform/shared-types';
import { api } from '@/lib/api-client';
import { formatNumber } from '@/lib/utils';
import { Select } from '@/components/shared/select';

type Granularity = 'day' | 'month';
type Breakdown = 'org' | 'segment';

interface MessagesResponse {
  granularity: Granularity;
  segments: { id: string; name: string }[];
  buckets: { period: string; total: number; bySegment: Record<string, number> }[];
}

const BRAND_FILL = 'rgb(var(--accent-300))';

// Stacked-bar palette — the action colour first, then well-separated steps of the branding palettes.
const SEGMENT_COLORS = [
  BRAND_FILL,
  'rgb(var(--primary-800))',
  'rgb(var(--primary-500))',
  'rgb(var(--accent-600))',
  'rgb(var(--neutral-500))',
  'rgb(var(--primary-300))',
  'rgb(var(--accent-800))',
  'rgb(var(--primary-600))',
  'rgb(var(--neutral-400))',
];

export function Dashboard(): JSX.Element {
  const [granularity, setGranularity] = useState<Granularity>('month');
  const [breakdown, setBreakdown] = useState<Breakdown>('org');

  const usage = useQuery({
    queryKey: ['usage-aggregate'],
    queryFn: () =>
      api.get<{ totalTokens: number; totalInputTokens: number; totalOutputTokens: number }>(
        '/api/admin/usage/aggregate?scope=ORG',
      ),
  });

  // One summary endpoint instead of the three list queries — the dashboard is ADMIN-only and
  // some of those lists are scoped to other roles by the server (EW-65).
  const summary = useQuery({
    queryKey: ['/api/admin/dashboard/summary'],
    queryFn: () => api.get<AdminDashboardSummary>('/api/admin/dashboard/summary'),
  });

  const messages = useQuery({
    queryKey: ['usage-messages', granularity],
    queryFn: () => api.get<MessagesResponse>(`/api/admin/usage/messages?granularity=${granularity}`),
  });

  const cards = [
    { label: 'Providers', value: summary.data?.providerCount ?? 0 },
    { label: 'Segments', value: summary.data?.segmentCount ?? 0 },
    { label: 'Users', value: summary.data?.userCount ?? 0 },
    { label: 'Token used (this month)', value: formatNumber(usage.data?.totalTokens ?? 0) },
  ];

  const segList = messages.data?.segments ?? [];
  const buckets = messages.data?.buckets ?? [];

  // Shape rows for recharts: org → single "Üzenetek" series; segment → one series per segment.
  const chartData =
    breakdown === 'org'
      ? buckets.map((b) => ({ period: b.period, Üzenetek: b.total }))
      : buckets.map((b) => {
          const row: Record<string, string | number> = { period: b.period };
          for (const s of segList) row[s.name] = b.bySegment[s.id] ?? 0;
          return row;
        });

  const hasData = buckets.length > 0;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-heading">Dashboard</h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="card p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{c.label}</p>
            <p className="mt-2 text-2xl font-bold text-heading">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="card p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-heading">Küldött üzenetek</h3>
          <div className="flex items-center gap-2">
            <Select
              className="w-32"
              value={granularity}
              onChange={(v) => setGranularity(v as Granularity)}
              options={[
                { value: 'day', label: 'Napi' },
                { value: 'month', label: 'Havi' },
              ]}
            />
            <Select
              className="w-44"
              value={breakdown}
              onChange={(v) => setBreakdown(v as Breakdown)}
              options={[
                { value: 'org', label: 'Teljes org' },
                { value: 'segment', label: 'Szegmensenként' },
              ]}
            />
          </div>
        </div>

        {hasData ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--neutral-200))" vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 12, fill: 'rgb(var(--neutral-500))' }}
                tickLine={false}
                axisLine={{ stroke: 'rgb(var(--neutral-200))' }}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: 'rgb(var(--neutral-500))' }}
                tickLine={false}
                axisLine={false}
                width={40}
              />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid rgb(var(--neutral-200))', fontSize: 12 }}
                cursor={{ fill: 'rgb(var(--neutral-100))' }}
              />
              {breakdown === 'org' ? (
                <Bar dataKey="Üzenetek" fill={BRAND_FILL} radius={[4, 4, 0, 0]} maxBarSize={48} />
              ) : (
                <>
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {segList.map((s, i) => (
                    <Bar
                      key={s.id}
                      dataKey={s.name}
                      stackId="segments"
                      fill={SEGMENT_COLORS[i % SEGMENT_COLORS.length]}
                      maxBarSize={48}
                    />
                  ))}
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-[320px] items-center justify-center text-sm text-neutral-400">
            {messages.isLoading ? 'Betöltés…' : 'Még nincs üzenet-adat ehhez az időszakhoz.'}
          </div>
        )}
      </div>
    </div>
  );
}
